import { useState, useCallback, useEffect } from "react"
import { useDispatch, useSelector } from "react-redux"
import {
  getRoles,
  getRole,
  createRole,
  updateRole,
  deleteRole,
  checkRoleDeletion,
  getRoleStaff,
  assignPermissionToRole,
  selectRoles,
  selectRolesLoading,
  selectRole,
  selectRoleLoading,
  selectRoleStaff
} from "store/staff"

export const useRolesAccess = () => {
  const dispatch = useDispatch()

  const roles = useSelector(selectRoles)
  const rolesLoading = useSelector(selectRolesLoading)
  const selectedRoleDetails = useSelector(selectRole)
  const roleLoading = useSelector(selectRoleLoading)
  const assignedStaffForRole = useSelector(selectRoleStaff)

  const [selectedRoleForAccess, setSelectedRoleForAccess] = useState(null)
  const [isAddRoleModalOpen, setIsAddRoleModalOpen] = useState(false)
  const [isEditRoleModalOpen, setIsEditRoleModalOpen] = useState(false)
  const [isDeleteRoleModalOpen, setIsDeleteRoleModalOpen] = useState(false)
  const [editingRole, setEditingRole] = useState(null)
  const [deletingRole, setDeletingRole] = useState(null)
  const [deletionCheckData, setDeletionCheckData] = useState(null)

  useEffect(() => {
    dispatch(getRoles())
  }, [dispatch])

  useEffect(() => {
    if (roles.length > 0 && !selectedRoleForAccess) {
      const firstRole = roles[0]
      setSelectedRoleForAccess(firstRole)
      if (firstRole.id) {
        dispatch(getRole(firstRole.id))
      }
    }
  }, [roles, selectedRoleForAccess, dispatch])

  useEffect(() => {
    if (selectedRoleForAccess?.id) {
      dispatch(getRole(selectedRoleForAccess.id))
    }
  }, [selectedRoleForAccess?.id, dispatch])

  const handleAddRole = useCallback(() => {
    setIsAddRoleModalOpen(true)
  }, [])

  const handleRoleEdit = useCallback((role) => {
    setEditingRole(role)
    setIsEditRoleModalOpen(true)
  }, [])

  const handleRoleDelete = useCallback(
    async (role) => {
      setDeletingRole(role)

      if (role.id) {
        try {
          const response = await dispatch(checkRoleDeletion(role.id))
          setDeletionCheckData(response?.data || null)
        } catch (error) {
          console.error("Failed to check role deletion:", error)
          setDeletionCheckData(null)
        }
      }

      setIsDeleteRoleModalOpen(true)
    },
    [dispatch]
  )

  const handleConfirmDelete = useCallback(
    async (replacementRoleId) => {
      if (!deletingRole?.id) return

      try {
        const params = {}
        if (replacementRoleId) {
          params.replacement_role_id = replacementRoleId
        }

        await dispatch(deleteRole(deletingRole.id, params))
        await dispatch(getRoles())
        setIsDeleteRoleModalOpen(false)
        setDeletingRole(null)
        if (selectedRoleForAccess?.id === deletingRole.id && roles.length > 1) {
          setSelectedRoleForAccess(roles[0].id !== deletingRole.id ? roles[0] : roles[1])
        }
      } catch (error) {
        console.error("Failed to delete role:", error)
      }
    },
    [deletingRole, selectedRoleForAccess, roles, dispatch]
  )

  const handleCloseDeleteModal = useCallback(() => {
    setIsDeleteRoleModalOpen(false)
    setDeletingRole(null)
    setDeletionCheckData(null)
  }, [])

  const handleSaveNewRole = useCallback(
    async (roleData) => {
      try {
        const createResult = await dispatch(createRole(roleData))
        const createdRole = createResult?.data

        if (!createdRole?.id) {
          console.error("No role ID returned from API")
          return
        }

        const rolesResult = await dispatch(getRoles())

        await dispatch(getRole(createdRole.id))

        const updatedRoles = rolesResult?.data
        const newRole = updatedRoles?.find((role) => role.id === createdRole.id)

        if (newRole) {
          setSelectedRoleForAccess(newRole)
        } else {
          setSelectedRoleForAccess(createdRole)
        }

        setIsAddRoleModalOpen(false)
      } catch (error) {
        console.error("Failed to create role:", error)
        throw error
      }
    },
    [dispatch]
  )

  const handleSaveEditRole = useCallback(
    async (roleData) => {
      if (!editingRole?.id) return

      try {
        await dispatch(updateRole(editingRole.id, roleData))

        setEditingRole(null)

        await dispatch(getRoles())
        await dispatch(getRole(editingRole.id))

        setIsEditRoleModalOpen(false)
      } catch (error) {
        console.error("Failed to update role:", error)
        throw error
      }
    },
    [editingRole, dispatch]
  )

  const handleCloseAddModal = useCallback(() => {
    setIsAddRoleModalOpen(false)
  }, [])

  const handleCloseEditModal = useCallback(() => {
    setIsEditRoleModalOpen(false)
    setEditingRole(null)
  }, [])

  const handlePermissionChange = useCallback(
    async (roleId, permissionData) => {
      try {
        await dispatch(assignPermissionToRole(roleId, permissionData))
      } catch (error) {
        console.error("Failed to update permission:", error)
        throw error
      }
    },
    [dispatch]
  )

  return {
    roles,
    rolesLoading,
    roleLoading,
    assignedStaffForRole,
    selectedRoleForAccess,
    selectedRoleDetails,
    setSelectedRoleForAccess,
    isAddRoleModalOpen,
    isEditRoleModalOpen,
    isDeleteRoleModalOpen,
    editingRole,
    deletingRole,
    deletionCheckData,
    handleAddRole,
    handleRoleEdit,
    handleRoleDelete,
    handleConfirmDelete,
    handleCloseDeleteModal,
    handleSaveNewRole,
    handleSaveEditRole,
    handleCloseAddModal,
    handleCloseEditModal,
    handlePermissionChange
  }
}
